import type { PatientCareTeamAssignment } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ProviderAvatar } from '@/components/provider-avatar';
import type { Column } from '@/components/ui/data-table';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton, EmptyState, ErrorState } from '@/components/ui/state-views';
import { getPatientCareTeam } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

export function CareTeamTab({ patientId }: { patientId: number }) {
  const navigate = useNavigate();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['patient-care-team', patientId],
    queryFn: () => getPatientCareTeam(patientId),
  });

  if (error)
    return <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Care team is unavailable' />;
  if (isLoading) return <DataTableSkeleton columns={6} label='Loading care team' />;
  if (!data || data.length === 0) {
    return <EmptyState message='No providers assigned to this patient.' />;
  }

  const columns: Column<PatientCareTeamAssignment>[] = [
    {
      key: 'provider',
      header: 'Provider',
      render: (m) => (
        <div className='flex items-center gap-3'>
          <ProviderAvatar
            firstName={m.provider.firstName}
            lastName={m.provider.lastName ?? ''}
            photoUrl={m.provider.photoUrl}
            size='sm'
          />
          <div>
            <div className='font-medium text-neutral-900'>
              {m.provider.firstName} {m.provider.lastName}
            </div>
            <div className='text-neutral-500'>{m.provider.email}</div>
          </div>
        </div>
      ),
    },
    { key: 'role', header: 'Role', render: (m) => <span className='text-neutral-700'>{m.providerType}</span> },
    { key: 'type', header: 'Type', render: (m) => <span className='text-neutral-700'>{m.provider.type}</span> },
    {
      key: 'state',
      header: 'State',
      render: (m) => <span className='text-neutral-700'>{m.provider.state ?? '\u2014'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) => <span className='text-neutral-700'>{m.provider.isActive ? 'Active' : 'Inactive'}</span>,
    },
    {
      key: 'assigned',
      header: 'Assigned',
      render: (m) => <span className='text-neutral-700'>{formatDate(m.assignedAt)}</span>,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      keyFn={(m) => m.id}
      onRowClick={(m) => navigate(`/providers/${m.provider.providerId}`)}
    />
  );
}
